export const MODULE_VISIBILITY = {
  always: 'ALWAYS',
  date: 'DATE',
};

export function isVisible(module) {
  if (module.visibilityMode === MODULE_VISIBILITY.always) return true;

  if (module.visibilityMode === MODULE_VISIBILITY.date) {
    const today = Date.now();
    const start = new Date(module.visibilityStartDate).setHours(0, 0, 0, 0);
    const end = new Date(module.visibilityEndDate).setHours(23, 59, 59, 999);

    return start <= today && end >= today;
  }

  return false;
}

const SCORABLE_RESOURCE_TYPES = new Set(['evaluation', 'module_scorm', 'upload_marker']);

export function mustShowTotalScore(module) {
  return module.showScore && module.sequence.some(activity => SCORABLE_RESOURCE_TYPES.has(activity.resourceType));
}

export function totalScore(module) {
  const [acc, counter] = module.sequence
    ?.filter(
      activity =>
        ['evaluation', 'module_scorm'].includes(activity.resourceType) ||
        (activity.resourceType === 'upload_marker' && activity.finished) ||
        (activity.resourceType === 'html_doc' && activity.finished && activity.scorable)
    )
    ?.reduce(([score, activityCounter], activity) => [score + activity.score, activityCounter + 1], [0, 0]) ?? [0, 0];

  return counter > 0 ? Math.floor(acc / counter) : 0;
}

export function abandonsBySlotId(abandons, slotId) {
  return abandons?.find(abandon => {
    return abandon.trainingSessionSlots?.some(trainingSessionSlot => trainingSessionSlot.id === slotId);
  });
}

/**
 * Get the name of a training session.
 * @param {object} trainingSession Training session to get the name of.
 * @returns The name of the training session.
 */
export function trainingSessionName(trainingSession) {
  return trainingSession.customName || trainingSession.custom_name || trainingSession.name;
}

/**
 * Group a collection of training sessions based on their status.
 * Frozen sessions are included..
 * @param {any[]} trainingSessions Training sessions collection to sort and
 * group.
 * @returns {{ongoingSessions: any[], pastSessions: any[]}} Groups of
 * sorted training sessions.
 */
export function toSortedAndGroupedTrainingSessionsByStatus(trainingSessions) {
  const copy = [...trainingSessions];
  sortTrainingSessionByStartDate(copy);
  return toGroupedTrainingSessionsByOngoingStatus(copy);
}

/**
 * Sort a collection of training sessions into a new array.
 * @param {object[]} trainingSessions Training sessions collection to sort.
 * @returns A new, sorted by name array of training sessions.
 */
export function toSortedTrainingSessionsByName(trainingSessions) {
  const copy = [...trainingSessions];
  copy.sort((left, right) => trainingSessionName(left).localeCompare(trainingSessionName(right)));

  return copy;
}

/**
 * Sort a collection of training sessions by startDate.
 * @param {object[]} trainingSessions Training sessions collection to sort.
 */
export function sortTrainingSessionByStartDate(trainingSessions) {
  trainingSessions.sort((left, right) => {
    const leftStartDate = left.startDate || '3000-01-01';
    const rightStartDate = right.startDate || '3000-01-01';
    const date_comparison = leftStartDate.localeCompare(rightStartDate);
    if (date_comparison) {
      return date_comparison;
    }
    return trainingSessionName(left).localeCompare(trainingSessionName(right));
  });
}

/**
 * Group a collection of training sessions based on their status.
 * Frozen sessions are included.
 * @param {object[]} trainingSessions Training sessions collection to sort.
 * @returns {{ongoingSessions: object[]}, {pastSessions: object[]}} The training sessions grouped
 * by their respective status.
 */
export function toGroupedTrainingSessionsByOngoingStatus(trainingSessions) {
  const startOfToday = new Date().setHours(0, 0, 0, 0);
  return trainingSessions.reduce(
    (trainingSessions, trainingSession) => {
      const trainingSessionEndTimestamp = Date.parse(trainingSession.endDate);
      if (isNaN(trainingSessionEndTimestamp) || trainingSessionEndTimestamp > startOfToday) {
        trainingSessions.ongoingSessions.push(trainingSession);
      } else {
        trainingSessions.pastSessions.push(trainingSession);
      }

      return trainingSessions;
    },
    { ongoingSessions: [], pastSessions: [] }
  );
}

/**
 * Calculate the progress value of a training session.
 *
 * @param {Array<{sequence: {finished: boolean}[]}>} trainingSessionModules
 *    Training session whose progress value must be calculated
 * @returns The progress value of the given training session, between 0 and 1 included.
 */
export function trainingSessionProgressValue(trainingSessionModules) {
  if (!trainingSessionModules || trainingSessionModules.length === 0) {
    return 0;
  }

  return (
    trainingSessionModules.reduce(
      (moduleProgressValueTotal, module) => (moduleProgressValueTotal += moduleProgressValue(module.sequence)),
      0
    ) / trainingSessionModules.length
  );
}

/**
 * Calculate the progress value of a module.
 *
 * @param {Array<{finished: boolean}>} moduleSequence Module whose progress value is to be calculated.
 * @returns The progress value of the given module, between 0 and 1 included.
 */
export function moduleProgressValue(moduleSequence) {
  if (!moduleSequence || moduleSequence.length === 0) {
    return 0;
  }

  return moduleSequence.reduce((finishedCount, sequence) => (finishedCount += sequence.finished), 0) / moduleSequence.length;
}

/**
 * Get the item associated to the given activity in the given module.
 *
 * @param {ModuleSequenceItemPropType} activity Activity whose item to return.
 * @param {ModulePropType} module Module containing the activity and all activity items.
 * @returns The item of the given activity.
 */
export function activityItem(activity, module) {
  switch (activity?.resourceType) {
    case 'evaluation':
      return module.pending_evaluations.find(x => x.evaluation.id === activity.resourceId.toString());
    case 'document':
      return module.documents.find(x => x.id === activity.resourceId.toString());
    case 'embed':
      return module.embeds.find(x => x.id === activity.resourceId.toString());
    case 'module_scorm':
      return module.scorms.find(x => x.id === activity.resourceId.toString());
    case 'module_moodle':
      return module.moodles.find(x => x.id === activity.resourceId.toString());
    case 'livestream':
      return module.livestreams.find(x => x.id === activity.resourceId.toString());
    case 'html_doc':
      return module.htmlDocs.find(x => x.id === activity.resourceId.toString());
    case 'email_marker':
      return module.email_markers.find(x => x.id === activity.resourceId.toString());
    case 'upload_marker':
      return module.upload_markers.find(x => x.id === activity.resourceId.toString());
    default:
      return null;
  }
}

/**
 * Get the name of an activity.
 * The name is selected based on the presence of a `name` property or on the
 * resource type. A generic name can also be returned in some cases.
 * @param {{name?: string, resourceType: string}} activity Activity to get the name of
 * @param {(key: string) => string} translator Translator function
 * @returns The name of the activity
 */
export function getActivityName(activity, translator) {
  if (activity.name) {
    return activity.name;
  }

  // Only these activity types may be empty so we need a default name.
  if (['html_doc', 'email_marker', 'livestream'].includes(activity.resourceType)) {
    // This object to avoid dynamic translation keys
    const translationKeys = {
      html_doc: 'ModuleActivity.default_name_html_doc',
      email_marker: 'ModuleActivity.default_name_email_marker',
      livestream: 'ModuleActivity.default_name_livestream',
    };

    return translator(translationKeys[activity.resourceType]);
  }
}
